import React, { useMemo, useRef, useState } from 'react';
import {
  useForm,
  SubmitHandler,
  FieldErrors,
  FormProvider,
} from 'react-hook-form';
import {
  Grid,
  GridItem,
  Heading,
} from '@chakra-ui/react';
import { omit } from 'lodash';

import { NextAction, PaymentElementProps, PaymentElementResponse } from '../../../@types';
import { SupportedProcessors, SupportedPaymentMethods } from '../../../constants';
import { CardInputControl } from '../../ui-elements/CardInputControl';
import { InputControl } from '../../ui-elements/InputControl';
import { StripeInputControl } from '../../ui-elements/StripeInputControl';
import { CheckboxControl } from '../../ui-elements/CheckboxControl';
import { SelectControl } from '../../ui-elements/SelectControl';
import { countryList } from '../../../utils/countries';
import { PaymentProvider } from '../../providers/Provider';
import { useCommonOptionsStore } from '../../../store';
import { useTranslations } from '../../../translations/useTranslations';
import { queryPaymentMethodHash } from '../../../queries/methodHash';
import { nextActionHandler } from '../../../handlers/nextActionHandler';

import { CreditCardFormProps } from './creditCard.types';
import { CreditCardFormPropTypes } from './creditCard.prop-types';

type FormInputsType = Record<string, any>;
type CreditCardDefaultProps = PaymentElementProps['guest'] & { cardholder: string };

const layoutProps: Record<string, any> = {
  gap: 6,
  columnGap: 4,
};

const initFields: CreditCardDefaultProps = {
  firstName: '',
  lastName: '',
  email: '',
  cardholder: '',
  billingDetails: {
    billingAddress1: '',
    billingState: '',
    billingCity: '',
    billingZip: '',
    billingCountry: '',
    billingEmail: '',
  },
};

const guestToBilling = (
  guestData: PaymentElementProps['guest'],
) => ({
  ...guestData?.billingDetails,
});

const repackFormData = (data: any) => {
  const billingDetails = Object.fromEntries(
    Object.keys(data)
      .filter(key => key.startsWith('billing'))
      .map(key => [key, data[key]]),
  );
  const { cardNumber, cardholder, expiry, cvc, firstName, lastName } = data;
  return {
    firstName,
    lastName,
    ...((typeof cardNumber === 'string') && { cardNumber: cardNumber.replace(/ /g, '') }),
    cardholder,
    // eslint-disable-next-line no-unsafe-optional-chaining
    ...((typeof expiry === 'string') && { expMonth: +expiry?.split('/')[0]?.trim() || 0 }),
    // eslint-disable-next-line no-unsafe-optional-chaining
    ...((typeof expiry === 'string') && { expYear: 2000 + +expiry?.split('/')[1]?.trim() || 0 }),
    ...((typeof cvc === 'string') && { cvc }),
    billingDetails,
  };
};

const flattenDefaultValues = (props: CreditCardDefaultProps) => {
  const {
    billingDetails, ...rest
  } = props;
  return {
    ...rest, ...billingDetails,
  };
};

const sanitizeErrors = (errors: FieldErrors<FormInputsType>) => (
  Object.fromEntries(Object.entries(errors).map(([key, val]) => ([key, val?.message])))
);

const unpackErrorMessage = (
  errors: FieldErrors<FormInputsType>,
  name: string,
) => (errors[name] ? errors[name]?.message ? errors[name]?.message : true : false);

const CreditCardForm = React.forwardRef(({
  processor,
  onChange,
  onLoadStart,
  onLoadEnd,
  onError,
  onSubmit,
  guest,
  billingConfig,
  contactInformationConfig,
  currentErrorOnly = false,
}: CreditCardFormProps, forwardRef: any) => {
  const paymentProviderRef = useRef<any>();

  const [sameAsAddress, setSameAsAddress] = useState(false);

  const stripeTheme = useCommonOptionsStore(state => state.stripeTheme);
  const lang = useCommonOptionsStore(state => state.lang);
  const propertyId = useCommonOptionsStore(state => state.propertyId);
  const apiKey = useCommonOptionsStore(state => state.apiKey);

  const [t] = useTranslations(lang);

  const formMethods = useForm<FormInputsType>({
    mode: 'onChange',
    defaultValues: {
      ...flattenDefaultValues({
        ...initFields, ...omit(guest, ['billingDetails']),
      }),
      cardholder: `${guest?.firstName} ${guest?.lastName}`.trim(),
    },
  });

  const { clearErrors, handleSubmit, formState, getValues, setValue, trigger } = formMethods;

  const {
    errors,
    isLoading,
    isSubmitting,
  } = formState;

  // @ const errCount = React.useMemo(() => (errors ? Object.keys(errors).length : 0), [errors]);
  const hasBillingConfig = React
    .useMemo(() => Boolean(billingConfig && Object.keys(billingConfig).length), [billingConfig]);

  const guestDataAsBilling = useMemo(() => guestToBilling(guest), [guest]);

  React.useEffect(() => {
    if (onLoadStart && isLoading) onLoadStart();
    if (onLoadEnd && isLoading) onLoadEnd();
  }, [isLoading, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onLoadStart && isSubmitting) onLoadStart();
    if (onLoadEnd && isSubmitting) onLoadEnd();
  }, [isSubmitting, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onError) onError(sanitizeErrors(formState.errors));
  }, [formState, onError]);

  const onFieldChange = async (fieldName: string) => {
    if (onChange) onChange({ [fieldName]: getValues(fieldName) });
    // @ await trigger([fieldName]);
  };

  const onFieldBlur = async (fieldName: string) => {
    if (currentErrorOnly) clearErrors();
    await trigger([fieldName]);
    // eslint-disable-next-line max-len
    // @ if (onError && getFieldState(fieldName).error) onError({ [fieldName]: getFieldState(fieldName).error?.message });
  };

  const onSameAsAddressChange = (isChecked: any) => {
    const billingFields = isChecked ? guestDataAsBilling : { ...initFields.billingDetails };
    Object.entries(billingFields).forEach(([key, val]) => {
      setValue(key, val);
    });
    setSameAsAddress(isChecked);
  };

  const submitHandler: SubmitHandler<FormInputsType> = async (formData: any) => {
    window.console.info('Processing payment.');
    const isValid = await trigger();
    if (!isValid) return;

    const data = repackFormData(formData);
    // TODO: this to remove and replace with createPaymentMethod
    const token = await paymentProviderRef.current.processPayment(data);

    const { error, paymentMethod } = await paymentProviderRef.current.createPaymentMethod(
      SupportedPaymentMethods.CARD,
      data,
    );

    if (onError && error) onError({ createPaymentMethod: error || t('paymentMethodError') });

    // @ hashing payment method
    const {
      hash: paymentMethodHash,
      error: hashError,
    } = await queryPaymentMethodHash(+propertyId!, apiKey!, {
      type: 'stripe_token',
      stripe_token: {
        token: token.id,
      },
    });

    if (hashError || !paymentMethodHash) {
      if (onError) onError({ hashError: hashError || t('paymentMethodError') });
      return;
    }

    if (onSubmit) {
      const responseData: PaymentElementResponse = {
        ...data,
        ...(token?.id && { token: token.id }),
        paymentMethod: 'card',
        paymentMethodId: paymentMethod?.id,
        paymentMethodHash,
      };

      const nextAction: NextAction = await onSubmit({ ...responseData });
      await nextActionHandler(nextAction);
    }
  };

  const submitLegacyHandler: SubmitHandler<FormInputsType> = async (formData: any) => {
    window.console.info('Processing legacy payment.');
    const isValid = await trigger();
    if (!isValid) return;

    const data = repackFormData(formData);

    // @ hashing payment method for Adyen
    // const {
    //   hash: paymentMethodHash,
    //   error: hashError,
    // } = await queryPaymentMethodHash(+propertyId!, apiKey!, {
    //   type: 'stripe_token',
    //   stripe_token: {
    //     token: token.id,
    //   },
    // });

    // if (hashError || !paymentMethodHash) {
    //   if (onError) onError({ hashError: hashError || t('paymentMethodError') });
    //   return;
    // }

    if (onSubmit) {
      const responseData: PaymentElementResponse = {
        ...data,
        paymentMethod: 'card',
      };

      const nextAction: NextAction = await onSubmit({ ...responseData });
      await nextActionHandler(nextAction);
    }
  };

  const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
    await paymentProviderRef.current.test('submitErrorHandler');
    if (onError) onError(sanitizeErrors(errors));
  };

  React.useImperativeHandle(forwardRef, () => ({
    submit() {
      handleSubmit(
        (processor === SupportedProcessors.ADYEN)
          ? submitLegacyHandler
          : submitHandler,
        submitErrorHandler,
      )();
    },
    async validate() {
      return trigger();
    },
  }));

  return (
    <>
      <PaymentProvider processor={processor} ref={paymentProviderRef} />
      <FormProvider {...formMethods} >
        <div>
          {contactInformationConfig && <Heading
            className="section-heading"
            paddingTop={(layoutProps as any).gap}
            paddingBottom={(layoutProps as any).gap}
          >{t('contactInformation')}</Heading>}

          {contactInformationConfig && <Grid
            templateColumns="repeat(4, 1fr)"
            templateRows="repeat(auto-fill, 1fr)"
            gap={(layoutProps as any).gap}
            columnGap={(layoutProps as any).columnGap}
          >
            {contactInformationConfig?.firstName && <GridItem colSpan={2}>
              <InputControl
                name="firstName"
                label={t('firstName')}
                error={unpackErrorMessage(errors, 'firstName')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired={contactInformationConfig?.firstName?.required}
              />
            </GridItem>}
            {contactInformationConfig?.lastName && <GridItem colSpan={2}>
              <InputControl
                name="lastName"
                label={t('lastName')}
                error={unpackErrorMessage(errors, 'lastName')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired={contactInformationConfig?.lastName?.required}
              />
            </GridItem>}
          </Grid>}

          <Heading
            className="section-heading"
            paddingTop={(layoutProps as any).gap}
            paddingBottom={(layoutProps as any).gap}
          >{t('paymentDetails')}</Heading>

          <Grid
            templateColumns="repeat(4, 1fr)"
            templateRows="repeat(auto-fill, 1fr)"
            gap={(layoutProps as any).gap}
            columnGap={(layoutProps as any).columnGap}
          >
            <GridItem colSpan={4}>
              <InputControl
                name="cardholder"
                label={t('cardholderName')}
                error={unpackErrorMessage(errors, 'cardholder')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired
              />
            </GridItem>

            <GridItem colSpan={4}>
              {processor === SupportedProcessors.STRIPE && <StripeInputControl
                id="card-number"
                inputType="card"
                name="cardNumber"
                label={t('cardNumber')}
                error={unpackErrorMessage(errors, 'cardNumber')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                theme={stripeTheme?.cardInput}
                isRequired
              />}

              {processor === SupportedProcessors.ADYEN && <CardInputControl
                name="cardNumber"
                label={t('cardNumber')}
                error={unpackErrorMessage(errors, 'cardNumber')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                placeholder="1234 1234 1234 1234"
                isRequired
              />}

            </GridItem>
            <GridItem colSpan={2}>
              {processor === SupportedProcessors.STRIPE && <StripeInputControl
                id="card-expiry"
                inputType="expiry"
                name="expiry"
                label={t('cardExpire')}
                error={unpackErrorMessage(errors, 'expiry')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                theme={stripeTheme?.expiryInput}
                isRequired
              />}

              {processor === SupportedProcessors.ADYEN && <InputControl
                name="expiry"
                label={t('cardExpire')}
                error={unpackErrorMessage(errors, 'expiry')}
                rules={{
                  validate: {
                    valid: (val: any) => {
                      const year = (new Date().getFullYear()) % 100;
                      const month = new Date().getMonth() + 1;
                      const values = val.split('/');
                      if (+values[1] > year && +values[0] <= 12) return true;
                      if (+values[1] === year && +values[0] <= 12 && +values[0] >= month) return true;
                      return 'Invalid date';
                    },
                  },
                }}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                placeholder="MM/YY"
                mask="00/00"
                isRequired
              />}
            </GridItem>
            <GridItem colSpan={2}>
              {processor === SupportedProcessors.STRIPE && <StripeInputControl
                id="card-cvc"
                inputType="cvc"
                name="cvc"
                label="CVC"
                error={unpackErrorMessage(errors, 'cvc')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                theme={stripeTheme?.cvcInput}
                isRequired
              />}

              {processor === SupportedProcessors.ADYEN && <InputControl
                name="cvc"
                label="CVC"
                error={unpackErrorMessage(errors, 'cvc')}
                rules={{
                  minLength: {
                    value: 3, message: '3 digits',
                  },
                }}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                placeholder="000"
                mask="0000"
                isRequired
              />}
            </GridItem>
          </Grid>

          {hasBillingConfig && <Heading
            className="section-heading"
            paddingTop={(layoutProps as any).gap}
            paddingBottom={(layoutProps as any).gap}
          >{t('billingAddressDetails')}</Heading>}

          {hasBillingConfig && <Grid
            templateColumns="repeat(2, 1fr)"
            templateRows="repeat(auto-fill, 1fr)"
            gap={(layoutProps as any).gap}
            columnGap={(layoutProps as any).columnGap}
          >
            {guest && <GridItem colSpan={2}>
              <CheckboxControl
                name="fill_billing_details"
                label={t('sameAsAddress')}
                onChange={onSameAsAddressChange}
              />
            </GridItem>}
            {billingConfig?.billingAddress1 && <GridItem colSpan={2}>
              <InputControl
                name="billingAddress1"
                label={t('billingStreetAddress')}
                error={unpackErrorMessage(errors, 'billingAddress1')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired={billingConfig?.billingAddress1?.required}
                isDisabled={sameAsAddress && guestDataAsBilling.billingAddress1}
              />
            </GridItem>}
            {billingConfig?.billingAddress2 && <GridItem colSpan={2}>
              <InputControl
                name="billingAddress2"
                label={t('billingStreetAddress2')}
                error={unpackErrorMessage(errors, 'billingAddress2')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired={billingConfig?.billingAddress2?.required}
                isDisabled={sameAsAddress && guestDataAsBilling.billingAddress2}
              />
            </GridItem>}
            {billingConfig?.billingCountry && <GridItem colSpan={2}>
              <SelectControl
                name="billingCountry"
                label={t('billingCountry')}
                error={unpackErrorMessage(errors, 'billingCountry')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                list={countryList}
                isRequired={billingConfig?.billingCountry?.required}
                isDisabled={sameAsAddress && guestDataAsBilling.billingCountry}
              />
            </GridItem>}
            {billingConfig?.billingState && <GridItem>
              <InputControl
                name="billingState"
                label={t('billingState')}
                error={unpackErrorMessage(errors, 'billingState')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired={billingConfig?.billingState?.required}
                isDisabled={sameAsAddress && guestDataAsBilling.billingState}
              />
            </GridItem>}
            {billingConfig?.billingCity && <GridItem>
              <InputControl
                name="billingCity"
                label={t('billingCity')}
                error={unpackErrorMessage(errors, 'billingCity')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired={billingConfig?.billingCity?.required}
                isDisabled={sameAsAddress && guestDataAsBilling.billingCity}
              />
            </GridItem>}
            {billingConfig?.billingZip && <GridItem>
              <InputControl
                name="billingZip"
                label={t('billingZip')}
                error={unpackErrorMessage(errors, 'billingZip')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                isRequired={billingConfig?.billingZip?.required}
                isDisabled={sameAsAddress && guestDataAsBilling.billingZip}
              />
            </GridItem>}
            {billingConfig?.billingEmail && <GridItem colSpan={2}>
              <InputControl
                name="billingEmail"
                label={t('billingEmail')}
                error={unpackErrorMessage(errors, 'billingEmail')}
                onChange={onFieldChange}
                onBlur={onFieldBlur}
                rules={{
                  validate: (val: any) => (
                    !val || Array.isArray(val.match(/[^\s@]+@[^\s@]+\.[^\s@]+/gi))
                  ) || t('invalidEmail'),
                }}
                isRequired={billingConfig?.billingEmail?.required}
                isDisabled={sameAsAddress && guestDataAsBilling.billingEmail}
              />
            </GridItem>}
          </Grid>}
        </div>
      </FormProvider>
    </>
  );
});

CreditCardForm.propTypes = CreditCardFormPropTypes;

export { CreditCardForm };
